import { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { AGENCY_ACTIVE } from "../constants";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { Agency } from "../types";

interface PublicAgenciesOptions {
  /**
   * Also load active test-environment agencies. Only meaningful inside a
   * platform test session (the organization portal needs the names of the
   * sandbox agencies it is linked to). Test agencies are never listed
   * publicly otherwise, whatever their publicListing flag says.
   */
  includeTestAgencies?: boolean;
}

/**
 * Loads agencies that are active AND opted into public listing.
 * Uses a one-time fetch instead of a real-time listener to reduce
 * Firestore connection overhead at scale.
 */
export function usePublicAgencies(options: PublicAgenciesOptions = {}) {
  const includeTestAgencies = options.includeTestAgencies === true;
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const queries = [
      query(
        collection(db, "agencies"),
        where("status", "==", AGENCY_ACTIVE),
        where("publicListing", "==", true)
      ),
    ];
    if (includeTestAgencies) {
      queries.push(query(
        collection(db, "agencies"),
        where("status", "==", AGENCY_ACTIVE),
        where("isTestData", "==", true)
      ));
    }
    Promise.all(queries.map((q) => getDocs(q)))
      .then((snapshots) => {
        if (cancelled) return;
        const byId = new Map<string, Agency>();
        for (const snapshot of snapshots) {
          for (const d of snapshot.docs) {
            const agency = fromDoc<Agency>(d);
            if (agency.isTestData === true && !includeTestAgencies) continue;
            byId.set(agency.id, agency);
          }
        }
        setAgencies([...byId.values()]);
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load public agencies:", err);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [includeTestAgencies]);

  return { agencies, loading };
}
